/*
    Offline resource library  (HAP-1422)
    ------------------------------------
    A small, framework-light store that lets a visitor "Save for offline" any
    resource (sermon / article / talk). We persist two things:

      • Lightweight metadata  -> localStorage  (title, authors, body,
        transcription, image url, audio url, duration ...)
      • The audio file itself -> IndexedDB as a Blob, so playback works with
        zero connection and no Service-Worker range-request gymnastics.

    The Service Worker (public/offline-sw.js) separately caches the app shell
    + hashed build assets so the SPA can boot offline; this module never needs
    to talk to it directly beyond registration.

    Exposes a reactive singleton so any component can react to the saved list
    and the online/offline state.
*/

import { reactive, computed } from 'vue'
import { audioSrcFor, proxyS3 } from './audioSrc'

const INDEX_KEY = 'cfl:offline:index'
const DB_NAME = 'cfl-offline'
const DB_VERSION = 1
const MEDIA_STORE = 'media'

const supported = typeof window !== 'undefined' && 'indexedDB' in window && 'caches' in window

// ---- reactive state -------------------------------------------------------

function loadIndex() {
    try {
        return JSON.parse(window.localStorage.getItem(INDEX_KEY) || '[]')
    } catch (e) {
        return []
    }
}

const state = reactive({
    saved: loadIndex(),
    saving: {}, // id -> 'saving' | 'error'
    online: typeof navigator !== 'undefined' ? navigator.onLine : true,
})

if (typeof window !== 'undefined') {
    window.addEventListener('online', () => (state.online = true))
    window.addEventListener('offline', () => (state.online = false))
    // Stay in sync if another tab edits the library.
    window.addEventListener('storage', (e) => {
        if (e.key === INDEX_KEY) state.saved = loadIndex()
    })
}

function persist() {
    try {
        window.localStorage.setItem(INDEX_KEY, JSON.stringify(state.saved))
    } catch (e) {
        /* quota / private mode — saved list just won't persist */
    }
}

// ---- IndexedDB media blobs ------------------------------------------------

function openDb() {
    return new Promise((resolve, reject) => {
        const req = window.indexedDB.open(DB_NAME, DB_VERSION)
        req.onupgradeneeded = () => {
            const db = req.result
            if (!db.objectStoreNames.contains(MEDIA_STORE)) db.createObjectStore(MEDIA_STORE)
        }
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => reject(req.error)
    })
}

async function idbPut(key, blob) {
    const db = await openDb()
    return new Promise((resolve, reject) => {
        const tx = db.transaction(MEDIA_STORE, 'readwrite')
        tx.objectStore(MEDIA_STORE).put(blob, key)
        tx.oncomplete = () => resolve()
        tx.onerror = () => reject(tx.error)
    })
}

async function idbGet(key) {
    const db = await openDb()
    return new Promise((resolve, reject) => {
        const tx = db.transaction(MEDIA_STORE, 'readonly')
        const r = tx.objectStore(MEDIA_STORE).get(key)
        r.onsuccess = () => resolve(r.result || null)
        r.onerror = () => reject(r.error)
    })
}

async function idbDelete(key) {
    const db = await openDb()
    return new Promise((resolve) => {
        const tx = db.transaction(MEDIA_STORE, 'readwrite')
        tx.objectStore(MEDIA_STORE).delete(key)
        tx.oncomplete = () => resolve()
        tx.onerror = () => resolve()
    })
}

// ---- helpers --------------------------------------------------------------

// Delegated to helpers/audioSrc so the offline cache key and the URL the
// players actually request are produced by the same function. This used to be a
// local copy that rewrote only `cfl-mango.s3.amazonaws.com` and not
// `cfl-mango.s3.us-east-1.amazonaws.com`, which is live in current data — a
// us-east-1 sermon was therefore fetched from the raw bucket, which (per the
// note below) sends no CORS headers, so the blob never cached and the record
// was stored with hasAudio false. The reader got a green "Saved offline"
// confirmation and then a text-only page with no player.
function resolveAudioUrl(resource) {
    return audioSrcFor(resource) || null
}

// Route S3 images through the same-origin /s3 proxy so the offline fetch isn't
// blocked by CORS (the raw bucket has no CORS headers, so the image blob would
// otherwise never cache). Vimeo/other hosts pass through untouched.
function resolveImageUrl(resource) {
    const url = resource.image?.url || resource?.video?.thumbnail || null
    return proxyS3(url) || null
}

function toRecord(resource) {
    return {
        id: resource.id,
        slug: resource.slug || null,
        collection: resource.collection || 'resources',
        title: resource.title || 'Untitled',
        startDate: resource.startDate || null,
        duration: resource.duration || resource?.audio?.duration || resource?.video?.duration || null,
        resourceType: resource.resourceType || null,
        image: resolveImageUrl(resource),
        authors: (resource.authors || []).map((a) => ({ id: a.id, title: a.title, image: a.image?.url || null })),
        body: resource.body || null,
        transcription: resource.transcription || null,
        audioUrl: resolveAudioUrl(resource),
        savedAt: Date.now(),
    }
}

// object-URL cache so the library doesn't leak / recreate URLs each render
const objectUrls = {}

// ---- public API -----------------------------------------------------------

function isSaved(id) {
    return state.saved.some((r) => r.id === id)
}

async function save(resource) {
    if (!supported || !resource?.id) return false
    const id = resource.id
    state.saving = { ...state.saving, [id]: 'saving' }
    try {
        const record = toRecord(resource)

        if (record.audioUrl) {
            try {
                const res = await fetch(record.audioUrl, { mode: 'cors' })
                if (res.ok) {
                    await idbPut(`audio:${id}`, await res.blob())
                    record.hasAudio = true
                }
            } catch (e) {
                // Audio couldn't be cached (CORS / offline) — keep text content.
                record.hasAudio = false
            }
        }

        // Best-effort image for an offline thumbnail; ignore failures.
        if (record.image) {
            try {
                const res = await fetch(record.image, { mode: 'cors' })
                if (res.ok) {
                    await idbPut(`image:${id}`, await res.blob())
                    record.hasImage = true
                }
            } catch (e) {
                record.hasImage = false
            }
        }

        state.saved = [record, ...state.saved.filter((r) => r.id !== id)]
        persist()
        const { [id]: _, ...rest } = state.saving
        state.saving = rest
        return true
    } catch (e) {
        state.saving = { ...state.saving, [id]: 'error' }
        return false
    }
}

async function remove(id) {
    state.saved = state.saved.filter((r) => r.id !== id)
    persist()
    if (objectUrls[`audio:${id}`]) {
        URL.revokeObjectURL(objectUrls[`audio:${id}`])
        delete objectUrls[`audio:${id}`]
    }
    if (objectUrls[`image:${id}`]) {
        URL.revokeObjectURL(objectUrls[`image:${id}`])
        delete objectUrls[`image:${id}`]
    }
    await idbDelete(`audio:${id}`)
    await idbDelete(`image:${id}`)
}

// Rebuild a resource-shaped document from the offline library so the detail
// page (/resources/:id) can render with zero connection. Audio + hero image
// are served from their cached IndexedDB blobs; author avatars aren't cached,
// so they keep their original urls (which simply won't paint offline).
async function getResource(id) {
    const record = state.saved.find((r) => r.id === id)
    if (!record) return null
    const [audioUrl, imageUrl] = await Promise.all([
        record.hasAudio ? getBlobUrl('audio', id).catch(() => null) : null,
        record.hasImage ? getBlobUrl('image', id).catch(() => null) : null,
    ])
    const image = imageUrl || record.image
    return {
        id: record.id,
        slug: record.slug,
        collection: record.collection || 'resources',
        title: record.title,
        startDate: record.startDate,
        duration: record.duration,
        resourceType: record.resourceType,
        body: record.body,
        transcription: record.transcription,
        image: image ? { url: image } : null,
        authors: (record.authors || []).map((a) => ({ id: a.id, title: a.title, image: a.image ? { url: a.image } : null })),
        audio: audioUrl ? { url: audioUrl, duration: record.duration } : null,
        offline: true,
    }
}

async function getBlobUrl(kind, id) {
    const key = `${kind}:${id}`
    if (objectUrls[key]) return objectUrls[key]
    const blob = await idbGet(key)
    if (!blob) return null
    const url = URL.createObjectURL(blob)
    objectUrls[key] = url
    return url
}

async function estimate() {
    if (navigator?.storage?.estimate) {
        const { usage, quota } = await navigator.storage.estimate()
        return { usage, quota }
    }
    return { usage: 0, quota: 0 }
}

function register() {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
    // Register after load so it never competes with first paint.
    // NB: the ?v= query is a deliberate cache-buster — Cloudflare keys on the
    // full URL (incl. query string), so a fresh version dodges any stale
    // edge-cached response for the bare /offline-sw.js path. Bump on SW change.
    window.addEventListener('load', () => {
        navigator.serviceWorker.register('/offline-sw.js?v=2').catch((e) => console.warn('[offline] SW registration failed', e))
    })
}

const offline = {
    supported,
    state,
    savedCount: computed(() => state.saved.length),
    isSaved,
    save,
    remove,
    getBlobUrl,
    getResource,
    estimate,
    register,
}

export default function useOffline() {
    return offline
}
